import React from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { getToken } from "../api/auth";
import { CycleSummary, CycleStatus, BulkExtendPreviewResponse, listCycles, exportCycleExcel, bulkExtendPreview, bulkExtendCommit } from "../api/manageCyclesApi";

// ── Display helpers (shared with AdminCycleDetailPage) ──────────────────────
export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function statusStyle(status: CycleStatus): React.CSSProperties {
  const palette: Record<CycleStatus, { bg: string; fg: string }> = {
    Stage1:           { bg: "#deecf9", fg: "#004578" },
    Stage2:           { bg: "#fff4ce", fg: "#5d4a00" },
    PostProcessing:   { bg: "#f4e6f9", fg: "#5c2d91" },
    Completed:        { bg: "#dff6dd", fg: "#107c10" },
    SkippedNoMembers: { bg: "#f3f2f1", fg: "#605e5c" },
  };
  const p = palette[status];
  return {
    background: p.bg,
    color: p.fg,
    fontSize: 12,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 12,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  };
}

/** Human-friendly label for a status badge. */
export function statusLabel(status: CycleStatus): string {
  return status === "SkippedNoMembers" ? "Skipped — no members" : status;
}

/** Returns a short urgency badge for an upcoming/past deadline, or null when the deadline is far away. */
export function deadlineBadge(iso: string | null): { text: string; color: string } | null {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0)  return { text: `${-diffDays}d overdue`, color: "#d13438" };
  if (diffDays === 0) return { text: "today",                 color: "#ff8c00" };
  if (diffDays <= 3)  return { text: `${diffDays}d left`,     color: "#ff8c00" };
  if (diffDays <= 7)  return { text: `${diffDays}d left`,     color: "#605e5c" };
  return null;
}

function errorMessageFromAxios(err: any, fallback: string): string {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const serverMsg = typeof body === "object" && body !== null ? body.error : null;
  if (status === 401 || status === 403) {
    return "You don't have admin access. Members of the AccessReviewAdmins Entra group can view review cycles.";
  }
  if (serverMsg) return serverMsg;
  return fallback;
}

const AdminCyclesPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const navigate = useNavigate();

  const [activeCycles, setActiveCycles] = React.useState<CycleSummary[]>([]);
  const [pastCycles,   setPastCycles]   = React.useState<CycleSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = React.useState(false);

  // Per-row Excel export state for the Past section.
  const [exportingId, setExportingId] = React.useState<number | null>(null);
  const [exportError, setExportError] = React.useState<string | null>(null);

  const loadCycles = React.useCallback(async () => {
    if (!account) return;
    setLoading(true);
    setError(null);
    try {
      await getToken(instance, account);
      const [active, past] = await Promise.all([listCycles("active"), listCycles("past")]);
      setActiveCycles(active);
      setPastCycles(past);
    } catch (err) {
      setError(errorMessageFromAxios(err, "Failed to load review cycles. Please refresh the page."));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [account, instance]);

  React.useEffect(() => { loadCycles(); }, [loadCycles]);

  const handleExport = React.useCallback(async (c: CycleSummary) => {
    if (!account) return;
    setExportingId(c.id);
    setExportError(null);
    try {
      await getToken(instance, account);
      const { blob, fileName } = await exportCycleExcel(c.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(errorMessageFromAxios(err, `Failed to export the Excel for ${c.toolName} ${c.cycleName}.`));
      console.error(err);
    } finally {
      setExportingId(null);
    }
  }, [account, instance]);

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Review Cycles</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.92 }}>
            Active cycles in Stage 1, Stage 2, or PostProcessing, plus completed and skipped past cycles.
            Click a row to drill down; download regenerated evidence for any past cycle.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: 32 }}>
          {!loading && activeCycles.length > 0 && (
            <button onClick={() => setBulkOpen(true)} style={heroButtonStyle}>
              Bulk extend deadlines…
            </button>
          )}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <img src="/trivago-logo.png" alt="trivago" style={{ height: 36 }} />
            <span style={{ fontSize: 11, letterSpacing: "0.08em", opacity: 0.85, marginTop: 4 }}>IT Operations</span>
          </div>
        </div>
      </div>

      {bulkOpen && (
        <BulkExtendDialog
          onClose={() => setBulkOpen(false)}
          onCommitted={() => { setBulkOpen(false); loadCycles(); }}
        />
      )}

      {loading ? (
        <p>Loading cycles…</p>
      ) : error ? (
        <div style={errorBoxStyle}>{error}</div>
      ) : (
        <>
          {exportError && <div style={{ ...errorBoxStyle, marginBottom: 16 }}>{exportError}</div>}

          {/* ── Active ─────────────────────────────────────────────── */}
          <div style={sectionHeadingStyle}>
            Active
            <span style={sectionCountStyle}>{activeCycles.length}</span>
          </div>
          {activeCycles.length === 0 ? (
            <div style={emptyStateStyle}>
              <p style={{ margin: 0, fontSize: 15 }}>No active review cycles right now.</p>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "#605e5c" }}>
                Cycles appear here when one of the configured tools enters its scheduled review window.
              </p>
            </div>
          ) : (
            <CyclesTable
              cycles={activeCycles}
              variant="active"
              onRowClick={(id) => navigate(`/manage/cycles/${id}`)}
            />
          )}

          {/* ── Past ───────────────────────────────────────────────── */}
          <div style={sectionHeadingStyle}>
            Past
            <span style={sectionCountStyle}>{pastCycles.length}</span>
          </div>
          {pastCycles.length === 0 ? (
            <div style={emptyStateStyle}>
              <p style={{ margin: 0, fontSize: 15 }}>No completed or skipped cycles yet.</p>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "#605e5c" }}>
                Once a cycle finishes (Completed) or is skipped for having no members, it moves here.
                You can drill in or download its evidence Excel at any time.
              </p>
            </div>
          ) : (
            <CyclesTable
              cycles={pastCycles}
              variant="past"
              onRowClick={(id) => navigate(`/manage/cycles/${id}`)}
              onExport={handleExport}
              exportingId={exportingId}
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Cycles table (shared by the Active and Past sections) ────────────────────
// variant "active": shows the deadline urgency badge and a "View →" affordance.
// variant "past":   suppresses the urgency badge (terminal cycles), shows a
//                    combined S1+S2 progress, and adds a per-row "Download
//                    Excel" button (regenerated from the DB) alongside drill-down.
const CyclesTable: React.FC<{
  cycles: CycleSummary[];
  variant: "active" | "past";
  onRowClick: (id: number) => void;
  onExport?: (c: CycleSummary) => void;
  exportingId?: number | null;
}> = ({ cycles, variant, onRowClick, onExport, exportingId }) => {
  const isPast = variant === "past";
  return (
    <table style={tableStyle}>
      <colgroup>
        <col style={{ width: "14%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "16%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "12%" }} />
      </colgroup>
      <thead>
        <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
          <th style={thStyle}>Tool</th>
          <th style={thStyle}>Cycle</th>
          <th style={thStyle}>Status</th>
          <th style={thStyle}>Started</th>
          <th style={thStyle}>{isPast ? "Deadline" : "Active deadline"}</th>
          <th style={thStyle}>Progress</th>
          <th style={thStyle} title="Items escalated to Stage 2">Escalated</th>
          <th style={thStyle}></th>
        </tr>
      </thead>
      <tbody>
        {cycles.map((c) => {
          const inStage2 = c.status === "Stage2";
          const shownDeadline = isPast ? (c.stage2Deadline ?? c.stage1Deadline) : (inStage2 ? c.stage2Deadline : c.stage1Deadline);
          const badge = isPast ? null : deadlineBadge(shownDeadline);
          const reviewed = isPast ? (c.reviewed + c.s2Reviewed) : (inStage2 ? c.s2Reviewed : c.reviewed);
          const denom = isPast ? c.total : (inStage2 ? c.escalated : c.total);
          const pct = denom > 0 ? Math.round((reviewed / denom) * 100) : 0;
          const startedAt = inStage2 ? c.stage2StartedAt : c.stage1StartedAt;
          return (
            <tr
              key={c.id}
              onClick={() => onRowClick(c.id)}
              style={{
                borderBottom: "1px solid #eee",
                cursor: "pointer",
                background: "white",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
            >
              <td style={tdStyle}>
                <div style={{ fontWeight: 600 }}>{c.toolName}</div>
                <div style={{ fontSize: 11.5, color: "#888", fontFamily: "monospace" }}>{c.controlId}</div>
              </td>
              <td style={tdStyle}>{c.cycleName}</td>
              <td style={tdStyle}><span style={statusStyle(c.status)}>{statusLabel(c.status)}</span></td>
              <td style={{ ...tdStyle, fontSize: 13 }}>{formatDate(startedAt)}</td>
              <td style={tdStyle}>
                <div style={{ fontSize: 13 }}>{formatDate(shownDeadline)}</div>
                {badge && (
                  <span style={{
                    display: "inline-block", marginTop: 3,
                    fontSize: 11, fontWeight: 600,
                    padding: "1px 7px", borderRadius: 8,
                    background: badge.color === "#d13438" ? "#fde7e9" : badge.color === "#ff8c00" ? "#fff4ce" : "#f3f3f3",
                    color: badge.color,
                    textTransform: "uppercase", letterSpacing: "0.04em",
                  }}>{badge.text}</span>
                )}
              </td>
              <td style={tdStyle}>
                {denom > 0 ? (
                  <>
                    <div style={{ fontSize: 13 }}>{reviewed} / {denom} reviewed</div>
                    <div style={progressTrackStyle}>
                      <div style={{ ...progressFillStyle, width: `${pct}%` }} />
                    </div>
                  </>
                ) : (
                  <span style={{ fontSize: 13, color: "#888" }}>—</span>
                )}
              </td>
              <td style={tdStyle}>{c.escalated}</td>
              <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                {isPast && onExport ? (
                  <button
                    onClick={() => onExport(c)}
                    disabled={exportingId === c.id}
                    style={{ ...downloadBtnStyle, opacity: exportingId === c.id ? 0.6 : 1, cursor: exportingId === c.id ? "default" : "pointer" }}
                    title="Regenerate and download the evidence Excel from the database"
                  >
                    {exportingId === c.id ? "Preparing…" : "Download Excel"}
                  </button>
                ) : (
                  <span style={{ color: "#0078d4", fontWeight: 600, fontSize: 13 }}>View →</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  padding: 24,
  fontFamily: "Segoe UI, sans-serif",
  maxWidth: 1300,
  margin: "0 auto",
};

const heroStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  padding: "16px 24px",
  marginBottom: 24,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 16px",
  color: "#a4262c",
  fontSize: 13,
};

const emptyStateStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid #e1dfdd",
  borderRadius: 6,
  padding: "40px 24px",
  textAlign: "center",
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: "28px 0 10px",
  fontSize: 15,
  fontWeight: 700,
  color: "#323130",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const sectionCountStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#605e5c",
  background: "#f3f2f1",
  borderRadius: 10,
  padding: "2px 8px",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  tableLayout: "fixed",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
};

const progressTrackStyle: React.CSSProperties = {
  marginTop: 4,
  background: "#e1dfdd",
  borderRadius: 4,
  height: 6,
  overflow: "hidden",
};

const progressFillStyle: React.CSSProperties = {
  background: "#0078d4",
  height: "100%",
  transition: "width 0.3s ease",
};

const downloadBtnStyle: React.CSSProperties = {
  background: "white",
  color: "#0078d4",
  border: "1px solid #0078d4",
  padding: "5px 10px",
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: 4,
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};

const heroButtonStyle: React.CSSProperties = {
  background: "white",
  color: "#0078d4",
  border: "none",
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  border: "none",
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryBtnStyle: React.CSSProperties = {
  background: "white",
  color: "#323130",
  border: "1px solid #ccc",
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const dialogBackdropStyle: React.CSSProperties = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  background: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: 60,
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  background: "white",
  borderRadius: 6,
  padding: 24,
  width: "min(800px, 90vw)",
  maxHeight: "80vh",
  overflow: "auto",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  fontFamily: "Segoe UI, sans-serif",
};

const dialogTableWrapStyle: React.CSSProperties = {
  maxHeight: 320,
  overflowY: "auto",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
};

const dialogTableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const dialogRedWarningStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 14px",
  marginBottom: 12,
  color: "#a4262c",
  fontSize: 12.5,
  lineHeight: 1.5,
};

// Bulk extend dialog — modal that previews a bulk extension and commits it.
const BulkExtendDialog: React.FC<{ onClose: () => void; onCommitted: () => void }> = ({ onClose, onCommitted }) => {
  const [days,     setDays]     = React.useState<number>(2);
  const [preview,  setPreview]  = React.useState<BulkExtendPreviewResponse | null>(null);
  const [pLoading, setPLoading] = React.useState(false);
  const [pError,   setPError]   = React.useState<string | null>(null);
  const [committing, setCommitting] = React.useState(false);
  const [cError,     setCError]     = React.useState<string | null>(null);

  const loadPreview = React.useCallback(async (d: number) => {
    if (d < 1 || d > 30 || !Number.isInteger(d)) {
      setPError("Days must be an integer between 1 and 30.");
      setPreview(null);
      return;
    }
    setPLoading(true);
    setPError(null);
    try {
      const p = await bulkExtendPreview(d);
      setPreview(p);
    } catch (err) {
      setPError(errorMessageFromAxios(err, "Failed to fetch preview."));
      console.error(err);
    } finally {
      setPLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const handle = setTimeout(() => { loadPreview(days); }, 200);
    return () => clearTimeout(handle);
  }, [days, loadPreview]);

  async function handleCommit() {
    if (!preview) return;
    setCommitting(true);
    setCError(null);
    try {
      await bulkExtendCommit(days);
      onCommitted();
    } catch (err) {
      setCError(errorMessageFromAxios(err, "Failed to apply the bulk extension."));
      console.error(err);
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div style={dialogBackdropStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Bulk extend cycle deadlines</h3>
        <p style={{ fontSize: 13.5, lineHeight: 1.5, marginBottom: 12 }}>
          Adds the chosen number of days to the active deadline of every cycle currently in Stage 1 or Stage 2.
          Cycles whose new deadline lands past their trigger month are flagged below for Internal Audit consultation.
          An audit-log row is written per cycle under your name.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: 13.5, fontWeight: 600 }}>Extend by:</label>
          <input
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
            disabled={committing}
            style={{ width: 70, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }}
          />
          <span style={{ fontSize: 13 }}>day(s) (1–30)</span>
        </div>

        {pLoading && <p style={{ fontSize: 13, color: "#605e5c" }}>Loading preview…</p>}
        {pError   && <div style={errorBoxStyle}>{pError}</div>}

        {preview && !pLoading && (
          <>
            {preview.plan.length === 0 ? (
              <p style={{ fontSize: 13, fontStyle: "italic", color: "#605e5c" }}>
                No active Stage 1 / Stage 2 cycles to extend.
              </p>
            ) : (
              <>
                <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 13, flexWrap: "wrap" }}>
                  <span><strong>{preview.totalCycles}</strong> cycle(s) will be extended by {preview.days} day(s)</span>
                  {preview.beyondTriggerMonthCycles > 0 && (
                    <span style={{ color: "#a4262c", fontWeight: 600 }}>
                      ⚠ {preview.beyondTriggerMonthCycles} will land past their trigger month
                    </span>
                  )}
                </div>

                {preview.beyondTriggerMonthCycles > 0 && (
                  <div style={dialogRedWarningStyle}>
                    <strong>⚠ You are extending one or more Access Reviews beyond the end of this month!</strong>
                    {" "}Please consult with the Internal Audit team first, before making such a change.
                    Click <em>Cancel</em> if this has not been discussed yet.
                  </div>
                )}

                <div style={dialogTableWrapStyle}>
                  <table style={dialogTableStyle}>
                    <thead>
                      <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
                        <th style={thStyle}>Tool / Cycle</th>
                        <th style={thStyle}>Stage</th>
                        <th style={thStyle}>Current</th>
                        <th style={thStyle}>New</th>
                        <th style={thStyle}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.plan.map((row) => (
                        <tr key={row.cycleId} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600 }}>{row.toolName}</div>
                            <div style={{ fontSize: 11.5, color: "#888" }}>{row.cycleName}</div>
                          </td>
                          <td style={tdStyle}>{row.activeStage}</td>
                          <td style={{ ...tdStyle, fontSize: 12.5 }}>{formatDate(row.currentDeadline)}</td>
                          <td style={{ ...tdStyle, fontSize: 12.5, fontWeight: row.beyondTriggerMonth ? 600 : 400, color: row.beyondTriggerMonth ? "#a4262c" : "inherit" }}>
                            {formatDate(row.newDeadline)}
                          </td>
                          <td style={{ ...tdStyle, fontSize: 12 }}>
                            {row.beyondTriggerMonth && (
                              <div style={{ color: "#a4262c", fontWeight: 600 }}>⚠ Past trigger month</div>
                            )}
                            {!row.hasOrchestrationInstance && (
                              <div style={{ color: "#605e5c" }}>No orchestration — SQL updated only</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {cError && <div style={{ ...errorBoxStyle, marginTop: 12 }}>{cError}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={committing} style={secondaryBtnStyle}>
            Cancel
          </button>
          <button
            onClick={handleCommit}
            disabled={committing || !preview || preview.totalCycles === 0 || pLoading}
            style={primaryBtnStyle}
          >
            {committing
              ? "Extending…"
              : preview && preview.totalCycles === 0
                ? "Nothing to extend"
                : `Confirm & extend ${preview?.totalCycles ?? 0} cycle(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCyclesPage;
